// Settings Component Logic
import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef, NgZone, ApplicationRef, HostListener, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../shared/services/notification.service';

@Component({
    selector: 'app-settings',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './settings.html',
    styleUrls: ['./settings.scss']
})
export class SettingsComponent implements OnInit, OnDestroy {
    private themeObserver: MutationObserver | null = null;
    removeAvatar(): void {
        this.userInfo.avatar = '';
        this.selectedImage = null;
    }
    userInfo = {
        firstName: 'Mykola',
        lastName: 'Ledenov',
        mobile: '+91 98765 43210',
        email: 'name@email.com',
        birthday: '05 July 2025',
        accountId: 'AXX-45839-2025',
        avatar: '',
        consentId: '',
        consentHandle: '',
        productId: ''
    };

    // Validation errors
    validationErrors: { [key: string]: string } = {};

    activeTab = 'general';
    selectedLanguage = 'English';
    selectedTheme = 'System';
    isDarkMode = false;

    // Dropdown visibility
    showLanguageDropdown = false;
    showThemeDropdown = false;

    // Modals
    showCropModal = false;
    showDisconnectModal = false;
    selectedBankToDisconnect: any = null;

    // Cropping Logic
    selectedImage: string | null = null;
    imageScale = 1;
    translateX = 0;
    translateY = 0;

    private isDragging = false;
    private startX = 0;
    private startY = 0;

    @ViewChild('cropCanvas') cropCanvas!: ElementRef<HTMLCanvasElement>;
    @ViewChild('sourceImage') sourceImage!: ElementRef<HTMLImageElement>;
    @ViewChild('avatarInput') avatarInput!: ElementRef<HTMLInputElement>;

    // Notification Toggles
    notifications = {
        payments: true,
        bills: false,
        ccAlerts: true,
        loans: false,
        critical: true
    };

    connectedBanks = [
        { id: 1, name: 'HDFC Bank', logo: 'assets/icons/img_image-1.png', connectedDate: '12 Feb 2026', status: 'connected' },
        { id: 2, name: 'Punjab National Bank', logo: 'assets/icons/img_frame-1996664442.png', connectedDate: '12 Feb 2026', status: 'connected' },
        { id: 3, name: 'Punjab National Bank', logo: 'assets/icons/img_frame-1996664442.png', connectedDate: '11 Feb 2026', status: 'connected' },
        { id: 4, name: 'Punjab National Bank', logo: 'assets/icons/img_frame-1996664442.png', connectedDate: '11 Feb 2026', status: 'disconnected' }
    ];

    constructor(
        private router: Router,
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone,
        private appRef: ApplicationRef,
        private notificationService: NotificationService
    ) { }

    ngOnInit(): void {
        this.loadUserInfo();
        this.syncThemeState();
        this.setupThemeObserver();
    }

    ngOnDestroy(): void {
        if (this.themeObserver) {
            this.themeObserver.disconnect();
        }
    }

    private setupThemeObserver(): void {
        if (typeof document === 'undefined') return;

        this.themeObserver = new MutationObserver(() => {
            this.ngZone.run(() => {
                this.syncThemeState();
                this.cdr.detectChanges();
            });
        });

        this.themeObserver.observe(document.body, {
            attributes: true,
            attributeFilter: ['class']
        });
    }

    private syncThemeState(): void {
        this.isDarkMode = document.body.classList.contains('dark-mode');
        this.selectedTheme = this.isDarkMode ? 'Dark' : 'Light';
    }

    loadUserInfo(): void {
        const storedMobile = localStorage.getItem('consent_mobile_number');
        if (storedMobile) this.userInfo.mobile = storedMobile;

        this.userInfo = {
            ...this.userInfo,
            accountId: localStorage.getItem('consent_account_id') || 'AXX-45839-2025',
            consentId: localStorage.getItem('active_consent_id') || 'No Active Consent',
            consentHandle: localStorage.getItem('active_consent_handle') || 'No Active Handle',
            productId: localStorage.getItem('consent_product_id') || 'TESTBANXX'
        };
    }

    setTab(tab: string): void {
        this.activeTab = tab;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        const isDropdownClick = target.closest('.custom-dropdown');

        if (!isDropdownClick) {
            this.showLanguageDropdown = false;
            this.showThemeDropdown = false;
        }
    }

    toggleLanguageDropdown(event: Event): void {
        event.stopPropagation();
        this.showLanguageDropdown = !this.showLanguageDropdown;
        if (this.showLanguageDropdown) this.showThemeDropdown = false;
    }

    selectLanguage(lang: string): void {
        this.selectedLanguage = lang;
        this.showLanguageDropdown = false;
    }

    toggleThemeDropdown(event: Event): void {
        event.stopPropagation();
        this.showThemeDropdown = !this.showThemeDropdown;
        if (this.showThemeDropdown) this.showLanguageDropdown = false;
    }

    selectTheme(theme: string): void {
        this.selectedTheme = theme;
        this.showThemeDropdown = false;
        if (theme === 'Dark' && !this.isDarkMode) {
            this.toggleTheme();
        } else if (theme === 'Light' && this.isDarkMode) {
            this.toggleTheme();
        }
    }

    toggleTheme(): void {
        document.body.classList.toggle('dark-mode');
        this.isDarkMode = !this.isDarkMode;
    }

    toggleNotification(key: keyof typeof this.notifications): void {
        if (key === 'critical') return; // Cannot disable critical
        this.notifications[key] = !this.notifications[key];
    }

    toggleNotifications(): void {
        this.notificationService.toggle();
    }

    // --- Image Selection & Dragging ---
    onAvatarClick(): void {
        const input = document.getElementById('avatarInput');
        if (input) {
            input.click();
        }
    }

    onFileSelected(event: any): void {
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e: any) => {
                this.ngZone.run(() => {
                    this.selectedImage = e.target.result;
                    this.imageScale = 1;
                    this.translateX = 0;
                    this.translateY = 0;
                    this.showCropModal = true;

                    // Manual change detection trigger to ensure immediate UI update
                    this.cdr.detectChanges();
                });
            };
            reader.readAsDataURL(file);

            // Reset input immediately so same file can be uploaded again
            if (input) {
                input.value = '';
            }
        }
    }

    openCropModalWithExisting(): void {
        if (this.userInfo.avatar) {
            this.selectedImage = this.userInfo.avatar;
            this.imageScale = 1;
            this.translateX = 0;
            this.translateY = 0;
            this.showCropModal = true;
            this.cdr.detectChanges();
        }
    }

    startDrag(event: MouseEvent | TouchEvent): void {
        this.isDragging = true;
        const pos = this.getEventPos(event);
        this.startX = pos.x - this.translateX;
        this.startY = pos.y - this.translateY;
        event.preventDefault();
    }

    onDrag(event: MouseEvent | TouchEvent): void {
        if (!this.isDragging) return;
        const pos = this.getEventPos(event);
        this.translateX = pos.x - this.startX;
        this.translateY = pos.y - this.startY;
    }

    stopDrag(): void {
        this.isDragging = false;
    }

    private getEventPos(event: MouseEvent | TouchEvent): { x: number, y: number } {
        if (event instanceof MouseEvent) {
            return { x: event.clientX, y: event.clientY };
        } else {
            return { x: event.touches[0].clientX, y: event.touches[0].clientY };
        }
    }

    onScaleChange(event: any): void {
        this.imageScale = parseFloat(event.target.value);
    }

    confirmCrop(): void {
        const canvas = this.cropCanvas.nativeElement;
        const img = this.sourceImage.nativeElement;
        const ctx = canvas.getContext('2d');

        if (ctx) {
            // High Quality High Resolution Crop (600x600)
            canvas.width = 600;
            canvas.height = 600;

            ctx.save();
            ctx.fillStyle = '#1a1a1a';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Draw circle mask for the persistent UI (optional for data URL)
            ctx.beginPath();
            ctx.arc(300, 300, 300, 0, Math.PI * 2);
            ctx.clip();

            // Calculate scale between displayed image and natural image
            const displaySize = 300; // CSS crop frame size
            const canvasScale = canvas.width / displaySize; // 2x for HD

            // The image is scaled to fit the crop frame, then zoomed by imageScale
            const baseScale = img.naturalWidth / img.width;
            const scale = this.imageScale * baseScale;

            // The center of the crop area in the original image
            const centerX = img.naturalWidth / 2 - (this.translateX * baseScale);
            const centerY = img.naturalHeight / 2 - (this.translateY * baseScale);

            // The crop box in the original image
            const cropSize = displaySize / this.imageScale * baseScale;
            const sx = centerX - cropSize / 2;
            const sy = centerY - cropSize / 2;
            const sWidth = cropSize;
            const sHeight = cropSize;

            ctx.drawImage(
                img,
                sx, sy, sWidth, sHeight,
                0, 0, canvas.width, canvas.height
            );

            ctx.restore();

            const croppedData = canvas.toDataURL('image/png', 1.0);
            this.userInfo.avatar = croppedData;
            this.closeCropModal();
        }
    }

    closeCropModal(): void {
        this.showCropModal = false;
        this.selectedImage = null;
    }

    openDisconnectModal(bank: any): void {
        this.ngZone.run(() => {
            this.selectedBankToDisconnect = bank;
            this.showDisconnectModal = true;
            this.cdr.detectChanges();
        });
    }

    closeDisconnectModal(): void {
        this.ngZone.run(() => {
            this.showDisconnectModal = false;
            this.selectedBankToDisconnect = null;
            this.cdr.detectChanges();
        });
    }

    confirmDisconnect(): void {
        if (this.selectedBankToDisconnect) {
            this.selectedBankToDisconnect.status = 'disconnected';
        }
        this.closeDisconnectModal();
    }

    logoutAllDevices(): void {
        localStorage.clear();
        this.router.navigate(['/request-consent']);
    }

    deleteAccount(): void {
        if (confirm('Are you sure you want to delete your account? This action is permanent.')) {

        }
    }

    saveSettings(): void {
        this.validationErrors = {};
        // Validate first name
        if (!this.userInfo.firstName || this.userInfo.firstName.trim().length < 2) {
            this.validationErrors['firstName'] = 'First name is required (min 2 characters).';
        }
        // Validate last name
        if (!this.userInfo.lastName || this.userInfo.lastName.trim().length < 2) {
            this.validationErrors['lastName'] = 'Last name is required (min 2 characters).';
        }
        // Validate email
        if (!this.userInfo.email || !/^\S+@\S+\.\S+$/.test(this.userInfo.email)) {
            this.validationErrors['email'] = 'Enter a valid email address.';
        }
        // Validate mobile (simple pattern for India)
        if (!this.userInfo.mobile || !/^\+91\s?\d{10}$/.test(this.userInfo.mobile)) {
            this.validationErrors['mobile'] = 'Enter a valid Indian mobile number (+91 XXXXXXXXXX).';
        }
        // Validate birthday (simple DD Month YYYY)
        if (!this.userInfo.birthday || !/^\d{2}\s\w+\s\d{4}$/.test(this.userInfo.birthday)) {
            this.validationErrors['birthday'] = 'Enter birthday as DD Month YYYY.';
        }

        if (Object.keys(this.validationErrors).length > 0) {
            // Do not proceed if errors
            return;
        }
    }

    goBack(): void {
        this.router.navigate(['/dashboard']);
    }
}
